/**
 * The artist Profile (spec `docs/superpowers/specs/2026-10-17-music24-artist-profile-design.md`,
 * design `docs/superpowers/design-reference/artist-profile/`): the pictures and the words of the
 * ARTIST, not of one song. Studio → Profile (`promote/promoteProfile`).
 *
 * This file is the one source of the kinds, the file sizes, the sources, the guides, the bio
 * limits, the Spotify upload rules and the API answer shapes. music24-api renders and checks with
 * it, the web app draws with it, MCP describes it. No imports outside fym-shared.
 */
import type { ProfileBannerFacts, ProfileBannerText } from './profileBanner';
import type { ReleaseThemeId } from './releaseThemes';
import { SPOTIFY_HEADER_ZONES, type HeaderBox } from './spotifyHeader';

// ---------------------------------------------------------------------------------------------
// Kinds and files
// ---------------------------------------------------------------------------------------------

/**
 * One kind of profile image. Each kind has one file, except `pressPhoto` (three crops). The Profile
 * has no X banner (owner decision 2026-10-20: Music24 has no X connector). Rows of the old kind
 * `xBanner` stay in the database; no reader lists them.
 */
export const ARTIST_PROFILE_KINDS = [
  'spotifyHeader',
  'spotifyAvatar',
  'youtubeBanner',
  'facebookCover',
  'pressPhoto',
] as const;
export type ArtistProfileKind = (typeof ARTIST_PROFILE_KINDS)[number];

export function isArtistProfileKind(value: unknown): value is ArtistProfileKind {
  return typeof value === 'string' && (ARTIST_PROFILE_KINDS as readonly string[]).includes(value);
}

/** The variant of a kind with one file. */
export const PROFILE_MAIN_VARIANT = 'main';
/** The three crops of a press photo. */
export const PRESS_PHOTO_VARIANTS = ['landscape', 'portrait', 'square'] as const;
export type PressPhotoVariant = (typeof PRESS_PHOTO_VARIANTS)[number];
export type ArtistProfileVariant = typeof PROFILE_MAIN_VARIANT | PressPhotoVariant;

/** The variants of a kind, in the order of the UI. Pure. */
export function profileVariantsOf(kind: ArtistProfileKind): readonly ArtistProfileVariant[] {
  return kind === 'pressPhoto' ? PRESS_PHOTO_VARIANTS : [PROFILE_MAIN_VARIANT];
}

export function isProfileVariantOf(kind: ArtistProfileKind, value: unknown): value is ArtistProfileVariant {
  return typeof value === 'string' && (profileVariantsOf(kind) as readonly string[]).includes(value);
}

/** Where the picture comes from. The same five sources as the kit Spotify header (`spotifyHeader.ts`). */
export const ARTIST_PROFILE_SOURCES = ['cover', 'photo', 'bank', 'ai', 'theme'] as const;
export type ArtistProfileSource = (typeof ARTIST_PROFILE_SOURCES)[number];

export function isArtistProfileSource(value: unknown): value is ArtistProfileSource {
  return typeof value === 'string' && (ARTIST_PROFILE_SOURCES as readonly string[]).includes(value);
}

/**
 * The file of one kind and variant (design b): the size, the box that every crop shows (the focus
 * point of a picture goes to its middle), the watermark place of a Free file, how the cover is
 * drawn (`blur`: the blurred cover with the theme wash; `plain`: the cover as it is), and the
 * sources the kind takes.
 */
export interface ProfileImageSpec {
  kind: ArtistProfileKind;
  variant: ArtistProfileVariant;
  width: number;
  height: number;
  maxBytes: number;
  /** The kit spec of the same file, when one exists (the kit renders it too). */
  kitSpecId: 'spotifyHeader' | 'bannerYoutube' | 'bannerFacebook' | null;
  subject: HeaderBox;
  /** The Free lockup: a `row` (or the `logo` alone under 200 px of free width), right edge and foot in shares. */
  watermark: { L: number; right: number; bottom: number };
  coverMode: 'blur' | 'plain';
  sources: readonly ArtistProfileSource[];
  /** The English label of the file name and the UI ("Spotify header"). */
  label: string;
}

const MB = 1024 * 1024;
const ALL_SOURCES = ARTIST_PROFILE_SOURCES;
/** A photo kind: no theme field and no blurred cover. */
const PHOTO_SOURCES: readonly ArtistProfileSource[] = ['photo', 'bank', 'ai'];

/**
 * The guides of each file. YouTube publishes its safe area (1546 x 423 in the middle of 2560 x
 * 1440). The Facebook box is the part that the computer crop (16:9 from the left, x 0 to 0.658)
 * and the phone crop (2.4:1 in the middle) both show, clear of the profile picture bottom left
 * (design profile-card b2: the old box, x 0.28 to 0.92, ran past the computer crop). The banner
 * texts use the same boxes (`PROFILE_BANNER_TEXT_BOXES`).
 */
const YOUTUBE_SAFE: HeaderBox = { x0: 0.198, x1: 0.802, y0: 0.353, y1: 0.647 };
const FACEBOOK_SUBJECT: HeaderBox = { x0: 0.09, x1: 0.63, y0: 0.14, y1: 0.6 };
const CENTRE: HeaderBox = { x0: 0.2, x1: 0.8, y0: 0.15, y1: 0.75 };

/** `<kind>.<variant>`. */
export type ProfileSpecKey = `${ArtistProfileKind}.${ArtistProfileVariant}`;

export const PROFILE_IMAGE_SPECS: Readonly<Partial<Record<ProfileSpecKey, ProfileImageSpec>>> = {
  'spotifyHeader.main': {
    kind: 'spotifyHeader',
    variant: 'main',
    width: 2660,
    height: 1140,
    maxBytes: 10 * MB,
    kitSpecId: 'spotifyHeader',
    subject: SPOTIFY_HEADER_ZONES.subject,
    watermark: { L: 40, right: 0.74, bottom: 0.8 },
    coverMode: 'blur',
    sources: ALL_SOURCES,
    label: 'Spotify header',
  },
  'spotifyAvatar.main': {
    kind: 'spotifyAvatar',
    variant: 'main',
    width: 1500,
    height: 1500,
    maxBytes: 10 * MB,
    kitSpecId: null,
    subject: CENTRE,
    watermark: { L: 60, right: 0.62, bottom: 0.86 },
    coverMode: 'plain',
    sources: ['photo', 'cover', 'bank', 'ai'],
    label: 'Artist photo',
  },
  'youtubeBanner.main': {
    kind: 'youtubeBanner',
    variant: 'main',
    width: 2560,
    height: 1440,
    maxBytes: 10 * MB,
    kitSpecId: 'bannerYoutube',
    subject: YOUTUBE_SAFE,
    // Outside the safe area, inside the desktop band: the mark never covers the text.
    watermark: { L: 34, right: 0.97, bottom: 0.63 },
    coverMode: 'blur',
    sources: ALL_SOURCES,
    label: 'YouTube banner',
  },
  'facebookCover.main': {
    kind: 'facebookCover',
    variant: 'main',
    width: 851,
    height: 315,
    maxBytes: 10 * MB,
    kitSpecId: 'bannerFacebook',
    subject: FACEBOOK_SUBJECT,
    // Under the text box, inside both crops and clear of the profile picture.
    watermark: { L: 16, right: 0.63, bottom: 0.9 },
    coverMode: 'blur',
    sources: ALL_SOURCES,
    label: 'Facebook cover',
  },
  'pressPhoto.landscape': {
    kind: 'pressPhoto',
    variant: 'landscape',
    width: 3000,
    height: 2000,
    maxBytes: 15 * MB,
    kitSpecId: null,
    subject: CENTRE,
    watermark: { L: 56, right: 0.95, bottom: 0.94 },
    coverMode: 'plain',
    sources: PHOTO_SOURCES,
    label: 'Press photo 3x2',
  },
  'pressPhoto.portrait': {
    kind: 'pressPhoto',
    variant: 'portrait',
    width: 2400,
    height: 3000,
    maxBytes: 15 * MB,
    kitSpecId: null,
    subject: CENTRE,
    watermark: { L: 56, right: 0.94, bottom: 0.95 },
    coverMode: 'plain',
    sources: PHOTO_SOURCES,
    label: 'Press photo 4x5',
  },
  'pressPhoto.square': {
    kind: 'pressPhoto',
    variant: 'square',
    width: 3000,
    height: 3000,
    maxBytes: 15 * MB,
    kitSpecId: null,
    subject: CENTRE,
    watermark: { L: 56, right: 0.95, bottom: 0.95 },
    coverMode: 'plain',
    sources: PHOTO_SOURCES,
    label: 'Press photo 1x1',
  },
};

/**
 * The spec of a kind and variant; with no variant, the first variant of the kind (the 3:2 press
 * crop for `pressPhoto`). Throws for a pair that does not exist. Pure.
 */
export function profileImageSpec(kind: ArtistProfileKind, variant?: ArtistProfileVariant): ProfileImageSpec {
  const picked = variant ?? profileVariantsOf(kind)[0] ?? PROFILE_MAIN_VARIANT;
  const spec = PROFILE_IMAGE_SPECS[`${kind}.${picked}`];
  if (spec === undefined) {
    throw new Error(`No profile image ${kind}.${picked}`);
  }
  return spec;
}

/** Every spec, in the order of the UI. */
export const PROFILE_IMAGE_SPEC_LIST: readonly ProfileImageSpec[] = ARTIST_PROFILE_KINDS.flatMap((kind) =>
  profileVariantsOf(kind).map((variant) => profileImageSpec(kind, variant))
);

/** The size of a file: the spec size, or half of it for a Free or trial preview (`PREVIEW_IMAGE_SCALE`). Pure. */
export function profileImageFileSize(
  spec: Pick<ProfileImageSpec, 'width' | 'height'>,
  preview: boolean
): {
  width: number;
  height: number;
} {
  if (!preview) {
    return { width: spec.width, height: spec.height };
  }
  return { width: Math.round(spec.width / 2), height: Math.round(spec.height / 2) };
}

/** The banners tool of the Studio makes these two kinds (design b2, with no X banner). */
export const PROFILE_BANNER_KINDS: readonly ArtistProfileKind[] = ['youtubeBanner', 'facebookCover'];

/** "Use this release for my profile" (design c3) makes these kinds from the blurred cover. */
export const PROFILE_FROM_RELEASE_KINDS: readonly ArtistProfileKind[] = ['spotifyHeader', ...PROFILE_BANNER_KINDS];

/**
 * The profiles that Music24 cannot reach (Profile "Send to all", design profile-card c2: "Set these
 * by hand"). The artist downloads the artist photo, puts it up and marks it "I set it". The mark is
 * stored on the version of the artist photo (`spotifyAvatar`), so a new version starts unmarked.
 */
export const PROFILE_MANUAL_SERVICES = [
  'instagram',
  'tiktok',
  'threads',
  'soundcloud',
  'appleMusic',
  'amazonMusic',
  'tidal',
  'bandcamp',
  'x',
] as const;
export type ProfileManualService = (typeof PROFILE_MANUAL_SERVICES)[number];

export function isProfileManualService(value: unknown): value is ProfileManualService {
  return typeof value === 'string' && (PROFILE_MANUAL_SERVICES as readonly string[]).includes(value);
}

/** The kind whose versions carry the marks of the manual services. */
export const PROFILE_MANUAL_MARK_KIND: ArtistProfileKind = 'spotifyAvatar';

/** The "I set it" marks of a version: service → when it was marked (ISO). */
export type ProfileManualMarks = Partial<Record<ProfileManualService, string>>;

/** The kinds that the Studio entry counts ("4 of 5 ready"): one per kind, the press photo counts once. */
export const PROFILE_COUNTED_KINDS: readonly ArtistProfileKind[] = ARTIST_PROFILE_KINDS;

/** `<Artist> - <Label>.jpg`, for example `LaziiBee - Spotify header.jpg`. The caller cuts it (`kitFileName`). */
export function profileFileLabel(spec: Pick<ProfileImageSpec, 'label'>): string {
  return spec.label;
}

// ---------------------------------------------------------------------------------------------
// The make request
// ---------------------------------------------------------------------------------------------

/** The look of a profile image: the artist look (the saved default) or the look of a release kit. */
export type ProfileLookFrom = 'artist' | 'release';

/** One item of `POST /accounts/:accountId/artist-profile/assets`. */
export interface ProfileMakeItem {
  kind: ArtistProfileKind;
  /** Omitted: every variant of the kind (the three press crops share one picture and one focus). */
  variant?: ArtistProfileVariant;
  source: ArtistProfileSource;
  /** `cover`: the release whose cover is used; also the release of `lookFrom: 'release'`. */
  catalogItemUuid?: string;
  /** `photo`: an image file of the account (the profile photo or an upload). */
  fileId?: number;
  /** `bank`: a published cover bank picture; `ai`: a ready AI picture of the account. */
  pictureUuid?: string;
  /** 0 to 1; the point of the picture that goes to the middle of the subject box. Default 0.5, 0.4. */
  focusX?: number;
  focusY?: number;
  /** Default `artist`. `release` needs `catalogItemUuid`. */
  lookFrom?: ProfileLookFrom;
  /** `spotifyAvatar` only: also set the new file as the Music24 artist photo (`AccountModel.avatarFileId`) when it is ready. */
  setAsAccountAvatar?: boolean;
  /**
   * `youtubeBanner` and `facebookCover` only (design profile-card b): the text on the banner.
   * Omitted: the variant of the newest banner, else `PROFILE_BANNER_TEXT_DEFAULT`. Other kinds ignore it.
   */
  bannerText?: ProfileBannerText;
}

export const PROFILE_MAKE_MAX_ITEMS = 10;
export const PROFILE_DEFAULT_FOCUS = { x: 0.5, y: 0.4 } as const;

/** The sources a kind takes. Pure. */
export function profileSourcesOf(kind: ArtistProfileKind): readonly ArtistProfileSource[] {
  return profileImageSpec(kind).sources;
}

/** The first source of a kind in the UI: the blurred cover, or the photo for a photo kind. Pure. */
export function profileDefaultSource(kind: ArtistProfileKind): ArtistProfileSource {
  return profileSourcesOf(kind)[0] ?? 'photo';
}

/** True when the artist can move the picture (`focusX`, `focusY`): a photo, a bank or an AI picture, or a plain cover. Pure. */
export function profileSourceMoves(kind: ArtistProfileKind, source: ArtistProfileSource): boolean {
  if (source === 'photo' || source === 'bank' || source === 'ai') {
    return true;
  }
  return source === 'cover' && profileImageSpec(kind).coverMode === 'plain';
}

/** The id that a source needs. Pure. */
export function profileSourceNeeds(source: ArtistProfileSource): 'file' | 'picture' | 'release' | null {
  if (source === 'photo') {
    return 'file';
  }
  if (source === 'bank' || source === 'ai') {
    return 'picture';
  }
  if (source === 'cover') {
    return 'release';
  }
  return null;
}

// ---------------------------------------------------------------------------------------------
// The bio
// ---------------------------------------------------------------------------------------------

/** The short bio fits every social profile (X 160). */
export const ARTIST_BIO_SHORT_MAX = 160;
/** The long bio fits Spotify for Artists (1500). */
export const ARTIST_BIO_LONG_MAX = 1500;
/** The facts the artist gives to the AI draft. */
export const ARTIST_BIO_FACTS_MAX = 600;
/** AI drafts per artist per day (UTC). */
export const ARTIST_BIO_DRAFTS_PER_DAY = 20;

/** The length of a text as the UI counts it (code points, like the store forms). Pure. */
export function bioLength(text: string): number {
  return Array.from(text).length;
}

/** Why a bio cannot be saved, or null. Pure. */
export function bioRefusal(bio: { bioShort: string; bioLong: string }): 'shortTooLong' | 'longTooLong' | null {
  if (bioLength(bio.bioShort) > ARTIST_BIO_SHORT_MAX) {
    return 'shortTooLong';
  }
  if (bioLength(bio.bioLong) > ARTIST_BIO_LONG_MAX) {
    return 'longTooLong';
  }
  return null;
}

// ---------------------------------------------------------------------------------------------
// Spotify upload (mapped live 2026-10-17, docs/music24-connectors/SPOTIFY-PROFILE-IMAGES.md)
// ---------------------------------------------------------------------------------------------

/** The two images that Spotify for Artists takes. */
export const SPOTIFY_PROFILE_IMAGES = ['header', 'avatar'] as const;
export type SpotifyProfileImage = (typeof SPOTIFY_PROFILE_IMAGES)[number];

/** The profile kind of a Spotify image. */
export const SPOTIFY_PROFILE_IMAGE_KIND: Readonly<Record<SpotifyProfileImage, ArtistProfileKind>> = {
  header: 'spotifyHeader',
  avatar: 'spotifyAvatar',
};

/** The rules of the Spotify upload page: JPEG, PNG or GIF; the header ≥ 2660 x 1140, the avatar ≥ 750 x 750. */
export const SPOTIFY_PROFILE_FILE_RULES: Readonly<
  Record<SpotifyProfileImage, { minWidth: number; minHeight: number; contentTypes: readonly string[] }>
> = {
  header: { minWidth: 2660, minHeight: 1140, contentTypes: ['image/jpeg', 'image/png', 'image/gif'] },
  avatar: { minWidth: 750, minHeight: 750, contentTypes: ['image/jpeg', 'image/png', 'image/gif'] },
};

/** The line of the Spotify page, shown before the first upload of an artist (design c2). */
export const SPOTIFY_PROFILE_RIGHTS_TEXT =
  'By uploading an image, you agree that you have all of the legal rights to do so as described in the Spotify terms.';
/** Change it when the text changes: a new version asks again. */
export const SPOTIFY_PROFILE_RIGHTS_VERSION = '2026-10-17';

/** Why a ready file cannot go to Spotify, or null. A Free preview never goes: it is half size and has the mark. Pure. */
export function spotifyProfileFileRefusal(
  image: SpotifyProfileImage,
  file: { width: number; height: number; watermark: boolean; preview: boolean; mimeType: string | null }
): 'preview' | 'tooSmall' | 'type' | null {
  if (file.preview || file.watermark) {
    return 'preview';
  }
  const rules = SPOTIFY_PROFILE_FILE_RULES[image];
  if (file.mimeType !== null && !rules.contentTypes.includes(file.mimeType)) {
    return 'type';
  }
  if (file.width < rules.minWidth || file.height < rules.minHeight) {
    return 'tooSmall';
  }
  return null;
}

/** The state of one Spotify image upload (design c2). */
export const SPOTIFY_PROFILE_UPLOAD_STATES = ['sending', 'onSpotify', 'failed', 'changedOnSpotify'] as const;
export type SpotifyProfileUploadState = (typeof SPOTIFY_PROFILE_UPLOAD_STATES)[number];

export const SPOTIFY_PROFILE_FAIL_REASONS = ['reconnect', 'noPermission', 'refused', 'fileGone', 'error'] as const;
export type SpotifyProfileFailReason = (typeof SPOTIFY_PROFILE_FAIL_REASONS)[number];

/** Why the Upload to Spotify button does not show or does not work (design c2.3–c2.8). */
export type SpotifyProfileUnavailable = 'noAutoUploadAccess' | 'notConnected' | 'reconnect' | 'freePlan' | null;

// ---------------------------------------------------------------------------------------------
// Spotify bio upload (mapped 2026-10-18, docs/music24-connectors/SPOTIFY-PROFILE-BIO.md)
// ---------------------------------------------------------------------------------------------

/**
 * The line of the Spotify bio editor, shown before the first bio upload of an artist. The image
 * line (`SPOTIFY_PROFILE_RIGHTS_TEXT`) is another text with its own acceptance.
 */
export const SPOTIFY_BIO_RIGHTS_TEXT =
  'By posting images, links, or a bio, you agree that you have all of the legal rights to do so.';
/** Change it when the text changes: a new version asks again. */
export const SPOTIFY_BIO_RIGHTS_VERSION = '2026-10-18';

/**
 * Why "Upload bio to Spotify" does not show or does not work. The same gates as the images, but a
 * Free or trial plan can send: a bio is text, it has no preview and no mark.
 */
export type SpotifyBioUnavailable = Exclude<SpotifyProfileUnavailable, 'freePlan'>;

/** Why the bio cannot go to Spotify, or null. Spotify takes the long bio (`ARTIST_BIO_LONG_MAX`). Pure. */
export function spotifyBioRefusal(bioLong: string): 'empty' | 'tooLong' | null {
  if (bioLong.trim() === '') {
    return 'empty';
  }
  return bioLength(bioLong) > ARTIST_BIO_LONG_MAX ? 'tooLong' : null;
}

/** A bio text as Spotify and Music24 compare it: one kind of line break, no space at the ends. Pure. */
export function normalizeBioText(text: string): string {
  return text.replace(/\r\n?/g, '\n').trim();
}

// ---------------------------------------------------------------------------------------------
// The API answers
// ---------------------------------------------------------------------------------------------

export type ProfileAssetStatus = 'queued' | 'rendering' | 'ready' | 'failed';

/** One version of a profile image. */
export interface SerializedProfileAsset {
  uuid: string;
  kind: ArtistProfileKind;
  variant: ArtistProfileVariant;
  version: number;
  status: ProfileAssetStatus;
  source: ArtistProfileSource;
  /** The release of a `cover` source or of `lookFrom: 'release'`. */
  catalogItemUuid: string | null;
  catalogItemTitle: string | null;
  /** The kit asset that "Use for my profile" took the picture from. */
  kitAssetUuid: string | null;
  /** `photo`: the source photo file of the account (not the made file: see `fileUuid`). */
  fileId: number | null;
  /** `bank` or `ai`: the source picture. */
  pictureUuid: string | null;
  focusX: number | null;
  focusY: number | null;
  look: { from: ProfileLookFrom; themeId: ReleaseThemeId; palette: string[] };
  /** The download of the file (`files/<uuid>/download`), or null. */
  fileUuid: string | null;
  url: string | null;
  /**
   * The address that imgproxy fetches the file from, or null. The app draws a thumbnail with
   * `drawnImageUrl` (owner rule 2026-10-19: every drawn image goes through imgproxy).
   */
  sourceUrl: string | null;
  /** A banner kind: the text variant of this version; null for the other kinds and old versions. */
  bannerText: ProfileBannerText | null;
  /** A banner with text: the facts the text was made from (the release title and date). Null otherwise. */
  bannerFacts: ProfileBannerFacts | null;
  width: number;
  height: number;
  preview: boolean;
  watermark: boolean;
  failCode: string | null;
  /** The file was removed (only the last 3 versions with a file keep it). */
  fileRemoved: boolean;
  /** True for the version that the Profile shows. */
  current: boolean;
  createdAt: string;
  readyAt: string | null;
  createdBy: { id: number; email: string } | null;
  /** The manual mark ("I set it") of a banner kind, or null. */
  setAt: string | null;
  /** The "I set it" marks of the manual services (`PROFILE_MANUAL_SERVICES`); only the artist photo has them. */
  manualMarks: ProfileManualMarks;
  spotify: {
    state: SpotifyProfileUploadState;
    failReason: SpotifyProfileFailReason | null;
    failDetail: string | null;
    at: string;
  } | null;
}

export interface ArtistProfileBio {
  bioShort: string;
  bioLong: string;
  updatedAt: string | null;
  updatedBy: { id: number; email: string } | null;
}

/** `GET /accounts/:accountId/artist-profile`. */
export interface ArtistProfileResponse {
  /** Per kind and variant: the current version (newest ready), and the newest one when it is working or failed. */
  assets: Array<{
    kind: ArtistProfileKind;
    variant: ArtistProfileVariant;
    current: SerializedProfileAsset | null;
    newest: SerializedProfileAsset | null;
  }>;
  bio: ArtistProfileBio;
  /** The artist look (the saved default) and the release it was saved from. */
  look: { themeId: ReleaseThemeId; palette: string[]; savedFrom: { catalogItemUuid: string; title: string } | null };
  /** The Music24 artist photo (Settings → Artist), or null. */
  avatar: { fileId: number; url: string; sourceUrl?: string } | null;
  /**
   * The newest release with a cover: the default release of the empty state and of the cover
   * source. `artworkSourceUrl`: an absolute address that imgproxy may fetch (the stored file, else
   * the cover URL of the claim), or null.
   */
  newestRelease: {
    catalogItemUuid: string;
    title: string;
    artworkUrl: string | null;
    artworkSourceUrl?: string | null;
  } | null;
  /**
   * The text of the banners (design profile-card b): the variant that a new banner gets (the
   * variant of the newest banner, else the default), the facts and the day (`YYYY-MM-DD`) of the
   * answer. Absent: an older API.
   */
  bannerText?: { variant: ProfileBannerText; facts: ProfileBannerFacts; today: string };
  /** True on Free and the trial: the files are half-size previews with the mark. */
  preview: boolean;
  spotify: {
    unavailable: SpotifyProfileUnavailable;
    rightsAccepted: boolean;
    rightsText: string;
    artistName: string | null;
    /** "Upload bio to Spotify": the long bio goes to the Spotify for Artists profile. */
    bio: {
      unavailable: SpotifyBioUnavailable;
      rightsAccepted: boolean;
      rightsText: string;
      /** The last bio upload that Spotify took, or null. */
      sentAt: string | null;
      /** True when the saved long bio is the text of the last upload. */
      sentIsCurrent: boolean;
    };
  };
}

/** `POST /accounts/:accountId/artist-profile/assets` and `.../from-release` → 201. */
export interface ProfileMakeResponse {
  created: SerializedProfileAsset[];
}

/** `POST /accounts/:accountId/artist-profile/bio/draft` → 200 (nothing is saved). */
export interface ArtistBioDraftResponse {
  bioShort: string;
  bioLong: string;
}

/** `POST /accounts/:accountId/artist-profile/zip` → 200. */
export interface ProfileZipResponse {
  url: string;
  fileName: string;
}

/** `POST /accounts/:accountId/artist-profile/spotify` → 201. */
export interface SpotifyProfileUploadResponse {
  uploads: Array<{ image: SpotifyProfileImage; assetUuid: string; state: SpotifyProfileUploadState }>;
}

/** `POST /accounts/:accountId/artist-profile/spotify/bio` → 200. */
export interface SpotifyBioUploadResponse {
  /** When Spotify took the bio. */
  sentAt: string;
  /**
   * True when the profile read after the upload shows the same text. False: Spotify took the call
   * (2xx), but the read did not show the text yet (or shows it in another form).
   */
  confirmed: boolean;
}

/** The error codes of the Profile routes (`cause.code`). */
export const ARTIST_PROFILE_ERROR_CODES = [
  'M24_PROFILE_SOURCE_INVALID',
  'M24_PROFILE_KIND_INVALID',
  'M24_PROFILE_ASSET_BUSY',
  'M24_PROFILE_BIO_TOO_LONG',
  'M24_PROFILE_BIO_DRAFT_LIMIT',
  'M24_PROFILE_NOTHING_READY',
  'M24_PROFILE_RIGHTS_REQUIRED',
  'M24_PROFILE_UPLOAD_FILE_REFUSED',
  'M24_AUTO_UPLOAD_NOT_ENABLED',
  'M24_PUBLISH_NOT_CONNECTED',
  'M24_PUBLISH_ALREADY_LIVE',
  'M24_PROFILE_SPOTIFY_NO_PERMISSION',
  'M24_PROFILE_SPOTIFY_REFUSED',
  'M24_PROFILE_SPOTIFY_FAILED',
] as const;
export type ArtistProfileErrorCode = (typeof ARTIST_PROFILE_ERROR_CODES)[number];
